import { createSignal, createResource, For, Show, createEffect } from 'solid-js';
import { useParams, useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import { showConfirm } from '../components/Dialog';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { ItemPresets } from '../models/ItemPresets';
import ItemStackView from '../components/ItemStackView';
import LocationPicker from '../components/LocationPicker';
import { LOCATION_KIND_LABELS, parseLocation } from '../services/gameLocation';
import { fromIdentifier } from '../services/itemRefAdapters';
import styles from './DropConfig.module.css';
import { Citybuild } from '../models/Citybuild';
import { formatDateTime } from '../services/dateFormat';

import deDE from '../de_DE.json';

interface DropConfig {
    id: string;
    userId: string;
    name: string;
    description: string;
    location: string;
    citybuild: Citybuild;
    itemsToDrop: string[];
    everythingExceptRepairable: boolean;
    createdAt: string;
    updatedAt: string;
    versionNumber: number;
}

export default function DropConfigPage() {
    const params = useParams();
    const navigate = useNavigate();
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = createSignal(false);
    const [saveLoading, setSaveLoading] = createSignal(false);
    const [showItemSelector, setShowItemSelector] = createSignal(false);
    const [itemSearchQuery, setItemSearchQuery] = createSignal('');
    
    const fetchDropConfig = async (id: string): Promise<DropConfig> => {
        const response = await api.minecraftConfig.getDropConfig(id);
        if (!isOk(response.data)) {
            throw new Error('Failed to fetch drop config');
        }
        return response.data.value as unknown as DropConfig;
    };
    
    const [dropConfig, { refetch }] = createResource(() => params.id, fetchDropConfig);
    const translations = deDE as any;

    const [formData, setFormData] = createSignal({
        id: params.id,
        userId: '',
        name: '',
        description: '',
        location: '',
        citybuild: Citybuild.CB1,
        itemsToDrop: [] as string[],
        everythingExceptRepairable: false,
        versionNumber: dropConfig()?.versionNumber || 1
    });

    createEffect(() => {
        const config = dropConfig();
        if (config) {
            setFormData({
                name: config.name,
                description: config.description,
                location: config.location,
                citybuild: config.citybuild,
                itemsToDrop: [...config.itemsToDrop],
                everythingExceptRepairable: config.everythingExceptRepairable || false,
                versionNumber: config.versionNumber || 1,
                userId: config.userId || '', 
                id: config.id || ''
            });
        }
    });

    const handleSave = async () => {
        setSaveLoading(true);
        const response = await api.minecraftConfig.updateDropConfig(formData() as never);
        setSaveLoading(false);

        if (isOk(response.data)) {
            setIsEditing(false);
            toast?.info(t('DROP_CONFIG_SAVED'));
            refetch();
        } else {
            toast?.error(t('DROP_CONFIG_SAVE_FAILED'));
        }
    };

    const handleDelete = async () => {
        if (!await showConfirm(t('DROP_CONFIG_DELETE_CONFIRM'), true)) return;

        const response = await api.minecraftConfig.deleteDropConfig(params.id);

        if (isOk(response.data)) {
            toast?.info(t('DROP_CONFIG_DELETED'));
            navigate('/dropconfigs');
        } else {
            toast?.error(t('DROP_CONFIG_DELETE_FAILED'));
        }
    };

    const removeItem = (itemId: string) => {
        setFormData(prev => ({
            ...prev,
            itemsToDrop: prev.itemsToDrop.filter(id => id !== itemId)
        }));
    };

    const getItemName = (itemId: string): string => {
        return translations?.[itemId] || itemId;
    };

    const applyPreset = (presetName: string) => {
        const preset = ItemPresets.getPresetByName(presetName);
        if (preset) {
            setFormData(prev => ({
                ...prev,
                itemsToDrop: [...new Set([...prev.itemsToDrop, ...preset.items])]
            }));
        }
    };

    const clearItems = () => {
        setFormData(prev => ({
            ...prev,
            itemsToDrop: []
        }));
    };

    const addItem = (itemId: string) => {
        if (!formData().itemsToDrop.includes(itemId)) {
            setFormData(prev => ({
                ...prev,
                itemsToDrop: [...prev.itemsToDrop, itemId]
            }));
        }
        // Do not close the modal after adding an item
        setItemSearchQuery('');
    };

    const getFilteredItems = () => {
        if (!translations) return [];
        const selected = formData().itemsToDrop;
        const query = itemSearchQuery().toLowerCase();
        return Object.keys(translations)
            .filter(itemId =>
                !selected.includes(itemId) &&
                (itemId.toLowerCase().includes(query) || translations[itemId].toLowerCase().includes(query))
            )
            .slice(0, 50);
    };

    return (
        <div class={styles.container}>
            <Show when={!dropConfig.loading} fallback={
                <div class={styles.loading}>
                    <i class="fa-solid fa-spinner fa-spin"></i>
                    <Text val="LOADING_DROP_CONFIG" />
                </div>
            }>
                <Show when={dropConfig.error} fallback={
                    <Show when={dropConfig()}>
                        {(config) => (
                            <div class={styles.content}>
                                <div class={styles.header}>
                                    <div class={styles.titleSection}>
                                        <h1>
                                            <Show when={!isEditing()} fallback={
                                                <input
                                                    class={styles.titleInput}
                                                    value={formData().name}
                                                    onInput={(e) => setFormData(prev => ({ ...prev, name: e.currentTarget.value }))}
                                                    placeholder={t('DROP_CONFIG_NAME_PLACEHOLDER')}
                                                />
                                            }>
                                                {config().name}
                                            </Show>
                                        </h1>
                                        <div class={styles.badges}>
                                            <Show when={parseLocation(isEditing() ? formData().location : config().location)}>
                                                {(location) => (
                                                    <span class={styles.badge}>
                                                        <Text val={LOCATION_KIND_LABELS[location().kind]} />
                                                    </span>
                                                )}
                                            </Show>
                                            
                                            <Show when={!isEditing()} fallback={
                                                <select
                                                    class={styles.citybuildSelect}
                                                    value={formData().citybuild}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, citybuild: parseInt(e.currentTarget.value) as Citybuild }))}
                                                >
                                                    <For each={Object.keys(Citybuild).filter(key => isNaN(Number(key)))}>
                                                        {(key) => (
                                                            <option value={Citybuild[key as keyof typeof Citybuild]}>
                                                                {Citybuild[key as keyof typeof Citybuild]}
                                                            </option>
                                                        )}
                                                    </For>
                                                </select>
                                            }>
                                                <span class={`${styles.badge} ${styles.citybuild}`}>
                                                    {Citybuild[config().citybuild]}
                                                </span>
                                            </Show>
                                        </div>

                                        <div class={styles.configItem}>
                                            <span class={styles.configLabel}>
                                                <Text val="EVERYTHING_EXCEPT_REPAIRABLE" />:
                                            </span>
                                            <Show when={isEditing()} fallback={
                                                <span class={`${styles.badge} ${config().everythingExceptRepairable ? styles.enabled : styles.disabled}`}>
                                                    <Text val={config().everythingExceptRepairable ? "ENABLED" : "DISABLED"} />
                                                </span>
                                            }>
                                                <label class={styles.checkboxLabel}>
                                                    <input
                                                        type="checkbox"
                                                        class={styles.checkbox}
                                                        checked={formData().everythingExceptRepairable}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, everythingExceptRepairable: e.currentTarget.checked }))}
                                                    />
                                                    <span class={styles.checkboxText}>
                                                        <Text val="EVERYTHING_EXCEPT_REPAIRABLE_HELP" />
                                                    </span>
                                                </label>
                                            </Show>
                                        </div>
                                    </div>
                                    
                                    <div class={styles.actions}>
                                        <Show when={!isEditing()} fallback={
                                            <>
                                                <button class={styles.saveButton} onClick={handleSave} disabled={saveLoading()}>
                                                    <i class="fa-solid fa-check"></i>
                                                    {saveLoading() ? '...' : <Text val="SAVE" />}
                                                </button>
                                                <button class={styles.cancelButton} onClick={() => setIsEditing(false)}>
                                                    <i class="fa-solid fa-times"></i>
                                                    <Text val="CANCEL" />
                                                </button>
                                            </>
                                        }>
                                            <button class={styles.editButton} onClick={() => setIsEditing(true)}>
                                                <i class="fa-solid fa-edit"></i>
                                                <Text val="EDIT" />
                                            </button>
                                            <button class={styles.deleteButton} onClick={handleDelete}>
                                                <i class="fa-solid fa-trash"></i>
                                                <Text val="DELETE" />
                                            </button>
                                        </Show>
                                    </div>
                                </div>

                                <div class={styles.grid}>
                                    <div class={styles.section}>
                                        <h2><Text val="DESCRIPTION" /></h2>
                                        <Show when={!isEditing()} fallback={
                                            <textarea
                                                class={styles.descriptionInput}
                                                value={formData().description}
                                                onInput={(e) => setFormData(prev => ({ ...prev, description: e.currentTarget.value }))}
                                                placeholder={t('DESCRIPTION')}
                                                rows={4}
                                            />
                                        }>
                                            <p class={styles.description}>{config().description}</p>
                                        </Show>
                                    </div>

                                    <div class={styles.section}>
                                        <h2><Text val="LOCATION" /></h2>
                                        <Show when={!isEditing()} fallback={
                                            <LocationPicker
                                                value={formData().location}
                                                onChange={(next) => setFormData(prev => ({ ...prev, location: next }))}
                                            />
                                        }>
                                            <div class={styles.location}>
                                                <i class="fa-solid fa-map-marker-alt"></i>
                                                <span>{config().location}</span>
                                            </div>
                                        </Show>
                                    </div>
                                </div>

                                <div class={styles.section}>
                                    <div class={styles.sectionHeader}>
                                        <h2>
                                            <Text val="ITEMS_TO_DROP" /> ({isEditing() ? formData().itemsToDrop.length : config().itemsToDrop.length})
                                        </h2>
                                        <Show when={isEditing()}>
                                            <button
                                                type="button"
                                                class={`${styles.addItemButton} ${formData().everythingExceptRepairable ? styles.disabled : ''}`}
                                                onClick={() => setShowItemSelector(true)}
                                                disabled={formData().everythingExceptRepairable}
                                                style={formData().everythingExceptRepairable ? 'pointer-events: none; opacity: 0.5; cursor: not-allowed;' : ''}
                                            >
                                                <i class="fa-solid fa-plus"></i>
                                                <Text val="ADD_ITEM" />
                                            </button>
                                        </Show>
                                    </div>
                                    
                                    <Show when={isEditing()}>
                                        <div class={`${styles.presets} ${formData().everythingExceptRepairable ? styles.disabled : ''}`} style={formData().everythingExceptRepairable ? 'pointer-events: none; opacity: 0.5;' : ''}>
                                            <div class={styles.presetsHeader}>
                                                <Text val="ITEM_PRESETS" />:
                                            </div>
                                            <div class={styles.presetButtons}>
                                                <For each={ItemPresets.ALL_PRESETS}>
                                                    {(preset) => (
                                                        <button
                                                            type="button"
                                                            class={styles.presetButton}
                                                            onClick={() => applyPreset(preset.name)}
                                                            disabled={formData().everythingExceptRepairable}
                                                        >
                                                            <i class={preset.icon}></i>
                                                            <Text val={preset.name} />
                                                        </button>
                                                    )}
                                                </For>
                                                <button
                                                    type="button"
                                                    class={styles.clearButton}
                                                    onClick={clearItems}
                                                    disabled={formData().everythingExceptRepairable}
                                                >
                                                    <i class="fa-solid fa-trash"></i>
                                                    <Text val="CLEAR_ALL" />
                                                </button>
                                            </div>
                                        </div>
                                    </Show>
                                    
                                    <div class={`${styles.itemsGrid} ${formData().everythingExceptRepairable ? styles.disabled : ''}`} style={formData().everythingExceptRepairable ? 'pointer-events: none; opacity: 0.5;' : ''}>
                                        <For each={isEditing() ? formData().itemsToDrop : config().itemsToDrop}>
                                            {(itemId) => (
                                                <div class={styles.itemCard}>
                                                    <ItemStackView stack={fromIdentifier(itemId)} titleOverride={itemId} />
                                                    <div class={styles.itemInfo}>
                                                        <div class={styles.itemName}>{getItemName(itemId)}</div>
                                                        <div class={styles.itemId}>{itemId}</div>
                                                    </div>
                                                    <Show when={isEditing() && !formData().everythingExceptRepairable}>
                                                        <button 
                                                            class={styles.removeItemButton}
                                                            onClick={() => removeItem(itemId)}
                                                        >
                                                            <i class="fa-solid fa-times"></i>
                                                        </button>
                                                    </Show>
                                                </div>
                                            )}
                                        </For>
                                    </div>
                                </div>

                                <div class={styles.metadata}>
                                    <div class={styles.metadataItem}>
                                        <span class={styles.metadataLabel}><Text val="CREATED" />:</span>
                                        <span>{formatDateTime(config().createdAt)}</span>
                                    </div>
                                    <div class={styles.metadataItem}>
                                        <span class={styles.metadataLabel}><Text val="LAST_UPDATED" />:</span>
                                        <span>{formatDateTime(config().updatedAt)}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Show>
                }>
                    <div class={styles.errorState}>
                        <i class="fa-solid fa-exclamation-triangle"></i>
                        <h3><Text val="ERROR_LOADING_DROP_CONFIG" /></h3>
                        <p>{dropConfig.error?.message}</p>
                    </div>
                </Show>
            </Show>

            <Show when={showItemSelector()}>
                <div class={styles.modal} onClick={() => setShowItemSelector(false)}>
                    <div class={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div class={styles.modalHeader}>
                            <h3><Text val="SELECT_ITEMS" /></h3>
                            <button 
                                type="button"
                                class={styles.modalClose}
                                onClick={() => setShowItemSelector(false)}
                            >
                                <i class="fa-solid fa-times"></i>
                            </button>
                        </div>
                        
                        <div class={styles.searchBox}>
                            <i class="fa-solid fa-search"></i>
                            <input
                                type="text"
                                placeholder={t('SEARCH_ITEMS_PLACEHOLDER')}
                                value={itemSearchQuery()}
                                onInput={(e) => setItemSearchQuery(e.currentTarget.value)}
                                class={styles.searchInput}
                            />
                        </div>

                        <div class={styles.itemsList}>
                            <For each={getFilteredItems()}>
                                {(itemId) => (
                                    <div 
                                        class={styles.selectableItem}
                                        onClick={() => addItem(itemId)}
                                    >
                                        <div class={styles.itemIcon}>
                                            <ItemStackView stack={fromIdentifier(itemId)} />
                                        </div>
                                        <div class={styles.itemInfo}>
                                            <div class={styles.itemName}>{getItemName(itemId)}</div>
                                            <div class={styles.itemId}>{itemId}</div>
                                        </div>
                                    </div>
                                )}
                            </For>
                        </div>
                    </div>
                </div>
            </Show>
        </div>
    );
}
